// Finish a password reset. Deliberately does NOT auto-login: the user is sent
// to /login to sign in fresh, which is friction against an attacker who had
// brief inbox access.
//
// The name fields exist because a reset also ACTIVATES a never-verified account,
// mirroring the invite flow. They used to be shown to everybody with the label
// "(if setting up a new account)" doing the explaining -- so an ordinary member
// resetting a forgotten password was asked for their name by a form that then
// threw it away, since the server only writes a name when `wasInactive`.
// `/auth/reset-address` now reports that same flag, so the fields appear only in
// the case that uses them.

import { ApiClientError, resetAddress, resetPassword } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { passwordReveal } from './passwordReveal.js';

export const resetView: View = (outlet, ctx) => {
  const code = ctx.query.get('code') ?? '';

  if (!code) {
    outlet.append(
      el('main', { class: 'auth-card' }, [
        el('h1', {}, 'Invalid link'),
        el('p', {}, 'This reset link is missing its code. Request a new one.'),
        el('p', {}, [el('a', { href: '/forgot' }, 'Request a new link')]),
      ]),
    );
    return;
  }

  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Set new password');

  // No email field visible on this form. See verify.ts for the full history
  // of why this field looks the way it does, and why the fetched address
  // below is what actually restores Chrome's save-password prompt.
  const usernameHint = el('input', {
    type: 'text',
    name: 'username',
    autocomplete: 'username',
    style: 'position:absolute;width:1px;height:1px;overflow:hidden;left:-9999px;',
  }) as HTMLInputElement;

  // One call, two answers: the address for the password manager, and whether this
  // reset is really an activation. Resolved after first paint, so the name block
  // starts hidden and is revealed only if it is wanted -- a form that silently
  // grows two fields is better than one that asks and then discards.
  void resetAddress(code).then(({ address, needsName }) => {
    if (address) usernameHint.value = address;
    nameFields.hidden = !needsName;
  });

  const firstName = el('input', {
    id: 'firstName',
    name: 'firstName',
    type: 'text',
    autocomplete: 'given-name',
  }) as HTMLInputElement;

  const lastName = el('input', {
    id: 'lastName',
    name: 'lastName',
    type: 'text',
    autocomplete: 'family-name',
  }) as HTMLInputElement;

  const password = el('input', {
    id: 'password',
    name: 'password',
    type: 'password',
    required: true,
    autocomplete: 'new-password',
    minlength: 8,
  }) as HTMLInputElement;

  const passwordConfirm = el('input', {
    id: 'passwordConfirm',
    name: 'passwordConfirm',
    type: 'password',
    required: true,
    autocomplete: 'new-password',
    minlength: 8,
  }) as HTMLInputElement;

  // Wrapped in one container so a single `hidden` covers the labels too. A plain
  // div carries no author `display`, so `hidden` really does hide it -- see the
  // `.selection-bar` note in CLAUDE.md for the case where it does not.
  const nameFields = el('div', { hidden: true }, [
    el('label', { for: 'firstName' }, 'First name'),
    firstName,
    el('label', { for: 'lastName' }, 'Last name'),
    lastName,
  ]);

  const form = el('form', { method: 'post', action: '/reset', novalidate: true }, [
    usernameHint,
    nameFields,
    el('label', { for: 'password' }, 'New password (at least 8 characters)'),
    password,
    el('label', { for: 'passwordConfirm' }, 'Confirm new password'),
    passwordConfirm,
    passwordReveal([password, passwordConfirm]),
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;

    // Match locally too so an obvious mismatch never costs a round trip.
    if (password.value !== passwordConfirm.value) {
      error.textContent = 'Passwords do not match';
      error.hidden = false;
      return;
    }

    submit.setAttribute('disabled', 'disabled');
    try {
      await resetPassword({
        code,
        password: password.value,
        passwordConfirm: passwordConfirm.value,
        firstName: firstName.value.trim() || undefined,
        lastName: lastName.value.trim() || undefined,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      navigate('/login', true);
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  outlet.append(el('main', { class: 'auth-card' }, [el('h1', {}, 'Set a new password'), form]));
};
